import { lazy } from 'react'
import { Navigate, type RouteObject } from 'react-router-dom'
import { RequireAuth } from '@/auth/RequireAuth'
import { AppShell } from '@/components/layout/AppShell'
import DashboardPage from '@/pages/DashboardPage'
import { RequireApproved } from './guards'
import { adminRoutes } from './admin.routes'
import { auditEngagementRoutes } from './auditEngagements.routes'
import { auditPlanRoutes } from './auditPlan.routes'
import { moduleRoutes } from './module.routes'

/**
 * The authenticated application: a single `AppShell` wrapping every signed-in
 * route. Administration routes are composed in from `admin.routes`, each with
 * its own permission gate.
 *
 * Code splitting: the landing dashboard ships in the initial bundle, every other
 * page loads on first visit. `AppShell` provides the shared `<Suspense>`
 * boundary so the navigation chrome stays visible while a page loads.
 */
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
const SettingsLayout = lazy(() => import('@/pages/settings/SettingsLayout'))
const GeneralSettings = lazy(() => import('@/pages/settings/GeneralSettings'))
const PasswordSettings = lazy(() => import('@/pages/settings/PasswordSettings'))
const ForbiddenPage = lazy(() => import('@/pages/ForbiddenPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

export const appRoutes: RouteObject[] = [
  {
    element: (
      <RequireAuth>
        <RequireApproved>
          <AppShell />
        </RequireApproved>
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: (
          <RequireApproved>
            <DashboardPage />
          </RequireApproved>
        ),
      },
      {
        path: 'profile',
        element: (
          <RequireApproved>
            <ProfilePage />
          </RequireApproved>
        ),
      },
      {
        path: 'settings',
        element: (
          <RequireApproved>
            <SettingsLayout />
          </RequireApproved>
        ),
        children: [
          { index: true, element: <Navigate to="general" replace /> },
          { path: 'general', element: <GeneralSettings /> },
          { path: 'password', element: <PasswordSettings /> },
          // The Google Sign-In section now lives on the General page; keep old
          // links working.
          { path: 'connections', element: <Navigate to="general" replace /> },
        ],
      },
      ...auditEngagementRoutes,
      ...auditPlanRoutes,
      ...moduleRoutes,
      ...adminRoutes,
      { path: 'forbidden', element: <ForbiddenPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]
